import { useState } from "react";
import { Box, Typography, Collapse, IconButton, Tooltip } from "@mui/material";
import {
  Link,
  Link2,
  Check,
  Users,
  ChevronDown,
  Edit,
  Mail,
  Phone,
  MapPin,
  Trash2,
  MoreVertical,
  Palette,
  Archive,
  ArchiveRestore,
} from "lucide-react";

import { Card, CardContent, CardHeader } from "../../components/Card";
import { Button } from "../../components/Button";
import { Badge } from "../../components/Badge";
import { TeamMemberCard } from "./TeamMemberCard";
import { useTheme } from "../../theme/useTheme";
import PropTypes from "prop-types";
import apiClient from "../../api-client";
import { useAuth } from "../../components/contexts/AuthContext";
import { EmptyStateMessage } from "../../components/EmptyStateMessage";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../../components/DropdownMenu";

const formatPlanDate = (value) =>
  value ? new Date(value).toLocaleDateString("es-ES") : "—";

const getPlanStatus = (subscription) => {
  if (!subscription?.ends_at) {
    return `Desde ${formatPlanDate(subscription?.started_at)}`;
  }
  const msLeft = new Date(subscription.ends_at) - new Date();
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return "Finalizado";
  if (daysLeft === 0) return "Termina hoy";
  return daysLeft === 1 ? "Queda 1 día" : `Quedan ${daysLeft} días`;
};

const MetaSeparator = () => (
  <Box component="span" sx={{ opacity: 0.5 }}>
    ·
  </Box>
);

const metaLinkSx = (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  color: "inherit",
  textDecoration: "none",
  "&:hover": {
    color: theme.palette.primary.main,
    textDecoration: "underline",
  },
});

/**
 * Componente para mostrar información de una agencia y sus miembros
 */
export const AgencyCard = ({
  agency,
  members = [],
  onMemberClick,
  onEditClick,
  onBrandingClick,
  onDeleteClick,
  onToggleArchive,
  onViewClientStats,
  onAssignAgency,
  showAgencyBadges = false,
  isSuperAdmin = false,
}) => {
  const { theme } = useTheme();
  const { user: currentUser } = useAuth();
  const [invitationCopiedAdmin, setInvitationCopiedAdmin] = useState(false);
  const [invitationCopiedConsultant, setInvitationCopiedConsultant] =
    useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHoveringButtons, setIsHoveringButtons] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [inviteAnchorEl, setInviteAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  const invitationCopied = invitationCopiedAdmin || invitationCopiedConsultant;

  const clientStats = agency?.clientStats || {};
  const activeClientCount = Number.isFinite(Number(clientStats.active))
    ? Number(clientStats.active)
    : 0;

  const trimmedWebsite = agency?.website?.trim();
  const agencyWebsite = trimmedWebsite
    ? /^https?:\/\//i.test(trimmedWebsite)
      ? trimmedWebsite
      : `https://${trimmedWebsite}`
    : null;

  // Función auxiliar para copiar al portapapeles compatible con Safari
  const copyToClipboardFallback = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand("copy");
      textArea.remove();
      return true;
    } catch (error) {
      console.error("Fallback: No se pudo copiar", error);
      textArea.remove();
      return false;
    }
  };

  const copyToClipboard = async (text) => {
    // Intentar primero con la API moderna del portapapeles
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        return copyToClipboardFallback(text);
      }
    } else {
      // Usar método fallback para navegadores que no soportan la API o contextos no seguros
      return copyToClipboardFallback(text);
    }
  };

  const handleCopyInvitationAdmin = async () => {
    try {
      // Enviar POST para generar código de invitación de admin
      const response = await apiClient.post(
        `agencies/${agency.id}/generate_admin_invites/`
      );

      // El backend ahora devuelve la URL directamente
      const invitationLink = response.data.url;

      const success = await copyToClipboard(invitationLink);
      if (success) {
        setInvitationCopiedAdmin(true);
        setTimeout(() => setInvitationCopiedAdmin(false), 2000);
      }
    } catch (error) {
      console.error("Error copiando enlace admin:", error);
    }
  };

  const handleCopyInvitationConsultant = async () => {
    try {
      // Enviar POST para generar código de invitación de consultor
      const response = await apiClient.post(
        `agencies/${agency.id}/generate_consultant_invites/`
      );

      // El backend ahora devuelve la URL directamente
      const invitationLink = response.data.url;

      const success = await copyToClipboard(invitationLink);
      if (success) {
        setInvitationCopiedConsultant(true);
        setTimeout(() => setInvitationCopiedConsultant(false), 2000);
      }
    } catch (error) {
      console.error("Error copiando enlace consultor:", error);
    }
  };

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const buttonHoverProps = {
    onMouseEnter: () => setIsHoveringButtons(true),
    onMouseLeave: () => setIsHoveringButtons(false),
  };

  return (
    <Card>
      <CardHeader
        sx={{
          pb: 3,
          borderRadius: "12px",
          cursor: isHoveringButtons ? "default" : "pointer",
          transition: "background-color 0.2s ease-in-out",
          "&:hover": isHoveringButtons
            ? {}
            : {
              backgroundColor: theme.palette.action.hover,
            },
        }}
        onClick={() => !isHoveringButtons && setIsExpanded(!isExpanded)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              minWidth: 0,
              flex: "1 1 280px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                minWidth: 0,
              }}
            >
              {agencyWebsite ? (
                <Box
                  component="a"
                  href={agencyWebsite}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: theme.palette.text.primary,
                    textDecoration: "none",
                    transition: "color 0.2s ease-in-out",
                    "&:hover": {
                      color: theme.palette.primary.main,
                      textDecoration: "underline",
                    },
                  }}
                >
                  <Typography
                    component="span"
                    variant="h5"
                    sx={{ fontWeight: 500, fontSize: "1.125rem" }}
                  >
                    {agency.name}
                  </Typography>
                  <Link2 size={16} />
                </Box>
              ) : (
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 500, fontSize: "1.125rem" }}
                >
                  {agency.name}
                </Typography>
              )}

              {isSuperAdmin && (
                <Badge
                  size="sm"
                  color={agency.agencyType === "external" ? "orange" : "pink"}
                >
                  {agency.agencyType === "external" ? "No Serseo" : "Serseo"}
                </Badge>
              )}

              {/* Serseo agencies: the type badge already says their plan */}
              {isSuperAdmin && agency.agencyType === "external" && agency.subscription && (
                <Tooltip
                  title={`${formatPlanDate(agency.subscription.started_at)}${
                    agency.subscription.ends_at
                      ? ` → ${formatPlanDate(agency.subscription.ends_at)}`
                      : ""
                  }`}
                  arrow
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    <Badge
                      size="sm"
                      color={
                        agency.subscription.plan === "trial" ? "blue" : "gray"
                      }
                    >
                      {agency.subscription.plan_name}
                    </Badge>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                      }}
                    >
                      {getPlanStatus(agency.subscription)}
                    </Typography>
                  </Box>
                </Tooltip>
              )}

              {agency.isArchived && (
                <Badge
                  size="sm"
                  sx={{
                    backgroundColor: theme.palette.error.main,
                    color: theme.palette.common.white,
                    border: `1px solid ${theme.palette.error.main}`,
                  }}
                >
                  Archivada
                </Badge>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                columnGap: 1,
                rowGap: 0.5,
                flexWrap: "wrap",
                color: theme.palette.text.secondary,
                fontSize: "0.875rem",
              }}
            >
              <span>{members.length} miembros</span>
              {isSuperAdmin && (
                <>
                  <MetaSeparator />
                  <span>{activeClientCount} clientes activos</span>
                </>
              )}
              {agency.location && (
                <>
                  <MetaSeparator />
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                  >
                    <MapPin size={14} />
                    {agency.location}
                  </Box>
                </>
              )}
              {agency.email && (
                <>
                  <MetaSeparator />
                  <Box
                    component="a"
                    href={`mailto:${agency.email}`}
                    onClick={(event) => event.stopPropagation()}
                    sx={metaLinkSx(theme)}
                  >
                    <Mail size={14} />
                    {agency.email}
                  </Box>
                </>
              )}
              {agency.phone && (
                <>
                  <MetaSeparator />
                  <Box
                    component="a"
                    href={`tel:${agency.phone}`}
                    onClick={(event) => event.stopPropagation()}
                    sx={metaLinkSx(theme)}
                  >
                    <Phone size={14} />
                    {agency.phone}
                  </Box>
                </>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
              flex: { xs: "1 1 100%", md: "0 0 auto" },
              flexWrap: "wrap",
            }}
          >
            {currentUser?.role === "superadmin" ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setInviteAnchorEl(e.currentTarget);
                  }}
                  {...buttonHoverProps}
                  sx={{ gap: 1 }}
                >
                  {invitationCopied ? <Check size={16} /> : <Link size={16} />}
                  {invitationCopied ? "¡Enlace copiado!" : "Invitar"}
                  {!invitationCopied && <ChevronDown size={14} />}
                </Button>
                <DropdownMenuContent
                  anchorEl={inviteAnchorEl}
                  open={Boolean(inviteAnchorEl)}
                  onClose={() => setInviteAnchorEl(null)}
                  align="end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenuLabel>Copiar enlace de invitación</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyInvitationAdmin();
                      setInviteAnchorEl(null);
                    }}
                  >
                    <Link size={16} />
                    Como admin
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyInvitationConsultant();
                      setInviteAnchorEl(null);
                    }}
                  >
                    <Link size={16} />
                    Como consultor
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyInvitationConsultant();
                }}
                {...buttonHoverProps}
                sx={{ gap: 1 }}
              >
                {invitationCopiedConsultant ? (
                  <Check size={16} />
                ) : (
                  <Link size={16} />
                )}
                {invitationCopiedConsultant ? "¡Copiado!" : "Invitar Consultor"}
              </Button>
            )}

            {currentUser?.role === "superadmin" && (
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                {...buttonHoverProps}
                sx={{
                  width: "32px",
                  height: "32px",
                  padding: 0,
                  flexShrink: 0,
                }}
              >
                <MoreVertical size={16} />
              </IconButton>
            )}

            <DropdownMenuContent
              anchorEl={menuAnchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              align="end"
              onClick={(e) => e.stopPropagation()}
            >
              {currentUser?.role === "superadmin" && (
                <>
                  {onViewClientStats && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewClientStats(agency);
                        handleMenuClose();
                      }}
                    >
                      <Users size={16} />
                      Ver clientes
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onEditClick) {
                        onEditClick(agency.id);
                      }
                      handleMenuClose();
                    }}
                  >
                    <Edit size={16} />
                    Editar
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onBrandingClick?.(agency);
                      handleMenuClose();
                    }}
                  >
                    <Palette size={16} />
                    Marca
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleArchive(agency);
                      handleMenuClose();
                    }}
                  >
                    {agency.isArchived ? <ArchiveRestore size={16} /> : <Archive size={16} />}
                    {agency.isArchived ? "Desarchivar" : "Archivar"}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onDeleteClick) {
                        onDeleteClick(agency.id);
                      }
                      handleMenuClose();
                    }}
                    sx={{ color: theme.palette.error.main }}
                  >
                    <Trash2 size={16} />
                    Eliminar
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                transition: "transform 0.3s ease-in-out",
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              <ChevronDown
                size={20}
                style={{ color: theme.palette.text.secondary }}
              />
            </Box>
          </Box>
        </Box>
      </CardHeader>

      <Collapse
        in={isExpanded}
        timeout={{ enter: 400, exit: 300 }}
        unmountOnExit
        sx={{
          "& .MuiCollapse-wrapper": {
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          },
        }}
      >
        <CardContent
          sx={{
            opacity: isExpanded ? 1 : 0,
            transition: "opacity 0.3s ease-in-out 0.1s",
            mt: "-16px",
          }}
        >
          {members.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
              }}
            >
              {members.map((member, index) => (
                <Box
                  key={member.id}
                  sx={{
                    opacity: 0,
                    transform: "translateY(10px)",
                    animation: isExpanded
                      ? `slideInFade 0.5s ease-out ${index * 0.06}s forwards`
                      : "none",
                    "@keyframes slideInFade": {
                      "0%": {
                        opacity: 0,
                        transform: "translateY(10px)",
                      },
                      "100%": {
                        opacity: 1,
                        transform: "translateY(0)",
                      },
                    },
                  }}
                >
                  <TeamMemberCard
                    member={member}
                    onMemberClick={onMemberClick}
                    onAssignAgency={onAssignAgency}
                    showDropdown={false}
                    showAgencyBadges={showAgencyBadges}
                    isSuperAdmin={isSuperAdmin}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                color: theme.palette.text.secondary,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 3,
                opacity: 0,
                animation: isExpanded
                  ? "fadeIn 0.4s ease-in-out 0.2s forwards"
                  : "none",
                "@keyframes fadeIn": {
                  "0%": {
                    opacity: 0,
                  },
                  "100%": {
                    opacity: 1,
                  },
                },
              }}
            >
              <EmptyStateMessage icon={Users} message="Aún no hay miembros" />
            </Box>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

AgencyCard.propTypes = {
  agency: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    location: PropTypes.string,
    website: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    agencyType: PropTypes.string,
    isArchived: PropTypes.bool,
    clientStats: PropTypes.shape({
      total: PropTypes.number,
      active: PropTypes.number,
      archived: PropTypes.number,
      byPack: PropTypes.objectOf(PropTypes.number),
    }),
    subscription: PropTypes.shape({
      plan: PropTypes.string,
      plan_name: PropTypes.string,
      started_at: PropTypes.string,
      ends_at: PropTypes.string,
    }),
  }).isRequired,
  members: PropTypes.array,
  onMemberClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onBrandingClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  onToggleArchive: PropTypes.func.isRequired,
  onViewClientStats: PropTypes.func,
  onAssignAgency: PropTypes.func,
  showAgencyBadges: PropTypes.bool,
  isSuperAdmin: PropTypes.bool,
};
